import React, { useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Button,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  listNotices,
  deleteNotice,
  type NoticeListItem,
  type ListNoticesResult,
} from '../../endpoints/notices';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const activeFilterOptions = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const NoticeManagementList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [activeFilter, setActiveFilter] = useState<'true' | 'false' | 'all'>(
    'all'
  );
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);
  const [removeNotice, setRemoveNotice] = useState<NoticeListItem | null>(null);

  const navigate = useNavigate();
  const listParams = useMemo(
    () => ({
      page: page + 1,
      per_page: perPage,
      active: activeFilter === 'all' ? undefined : activeFilter,
    }),
    [page, perPage, activeFilter]
  );

  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useApiGet<ListNoticesResult>(
    ['notices', listParams],
    () => listNotices(listParams),
    { enabled: true, retry: 1 }
  );

  const { mutate: deleteMutate } = useApiSend(
    (id: string) => deleteNotice(id),
    () => {
      setRemoveNotice(null);
      setAlertState({
        severity: 'success',
        description:
          'Notice retired. It will no longer be shown to users and all recipients have been marked as completed.',
      });
      refetch();
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description:
          (err as any)?.response?.data?.error ||
          (err as any)?.message ||
          'Remove failed.',
      });
    },
    [['notices']]
  );

  const listItems = listData?.items ?? [];
  const totalCount = listData?.total_count ?? 0;

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRemoveConfirm = () => {
    if (removeNotice) {
      deleteMutate(removeNotice.id);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Notice Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create and manage notices that appear for selected recipients.
          Retiring a notice expires it and marks it as completed for all users.
        </Typography>
      </Grid>
      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Tabs
            value={activeFilter}
            onChange={(_, v: 'true' | 'false' | 'all') => setActiveFilter(v)}
            sx={{ minHeight: 40 }}
          >
            {activeFilterOptions.map((opt) => (
              <Tab key={opt.value} label={opt.label} value={opt.value} />
            ))}
          </Tabs>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/administration/notice-management/new')}
          >
            Create Notice
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <TableContainer>
            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box p={2}>
                <Typography color="error">
                  {(error as Error)?.message || 'Failed to load notices.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No notices found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    listItems.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.title}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          {row.description?.slice(0, 80)}
                          {row.description && row.description.length > 80
                            ? '…'
                            : ''}
                        </TableCell>
                        <TableCell>
                          {row.due_date
                            ? dayjs(row.due_date).format('MM/DD/YYYY')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.recipient_names?.length
                            ? row.recipient_names.join(', ')
                            : row.recipient_group_type_ids?.length
                              ? `${row.recipient_group_type_ids.length} recipient(s)`
                              : '—'}
                        </TableCell>
                        <TableCell>
                          {row.created_at
                            ? dayjs(row.created_at).format('MM/DD/YYYY')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label="Edit"
                            onClick={() =>
                              navigate(
                                `/administration/notice-management/${row.id}`
                              )
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Remove"
                            onClick={() => setRemoveNotice(row)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {listData && listItems.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={perPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </Paper>
      </Grid>
      <Dialog open={!!removeNotice} onClose={() => setRemoveNotice(null)}>
        <DialogTitle>Retire notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will expire the notice and mark it as completed for all
            recipients. The notice will no longer be shown. You can still see it
            in the list (as inactive). Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveNotice(null)}>Cancel</Button>
          <Button
            onClick={handleRemoveConfirm}
            color="primary"
            variant="contained"
          >
            Retire
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default NoticeManagementList;
